import React, { useState, useEffect } from 'react';
import {
  Share2,
  Lock,
  Unlock,
  Users,
  FileText,
  Award,
  MessageSquare,
  Eye,
  EyeOff,
  Settings,
  Clock,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  User,
  Calendar,
  School,
  MoreVertical,
  X,
  RefreshCw,
  Bell
} from 'lucide-react';
import { useSharingContext } from '../../contexts/SharingContext';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';

const StudentSharingPanel = () => {
  const {
    sharedItems,
    sharingPermissions,
    parentAccess,
    notifications,
    unreadCount,
    loading,
    shareItem,
    revokeSharing,
    updateCategoryPermissions,
    loadSharingData,
    getAvailableParents
  } = useSharingContext();

  const { currentUser } = useFamilyContext();
  const [expandedCategories, setExpandedCategories] = useState({
    essays: true,
    bragSheet: false,
    recommendations: false
  });
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const availableParents = getAvailableParents();

  // Refresh data periodically
  useEffect(() => {
    const interval = setInterval(() => {
      loadSharingData();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, []);

  const handleRevoke = async (itemType, itemId, recipientId) => {
    if (window.confirm('Are you sure you want to revoke sharing for this item?')) {
      await revokeSharing(itemType, itemId, recipientId);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getCategoryItems = (category) => {
    const typeMap = {
      essays: 'essay',
      bragSheet: 'brag_sheet',
      recommendations: 'recommendation'
    };
    return sharedItems[typeMap[category]] || [];
  };

  const calculateStats = () => {
    let totalShared = 0;
    let totalComments = 0;

    Object.values(sharedItems).forEach(categoryItems => {
      if (Array.isArray(categoryItems)) {
        totalShared += categoryItems.length;
        categoryItems.forEach(item => {
          if (item.comments && Array.isArray(item.comments)) {
            totalComments += item.comments.length;
          }
        });
      }
    });

    const parentsWithAccess = parentAccess?.length || 0;
    const lastShared = Object.values(sharedItems)
      .flat()
      .sort((a, b) => new Date(b.shared_at) - new Date(a.shared_at))[0];

    return {
      totalShared,
      parentsWithAccess,
      totalComments,
      lastSharedDate: lastShared?.shared_at
    };
  };

  const stats = calculateStats();

  const PermissionsModal = () => {
    const [localSettings, setLocalSettings] = useState({});

    useEffect(() => {
      if (selectedCategory && sharingPermissions[selectedCategory]) {
        setLocalSettings(sharingPermissions[selectedCategory]);
      }
    }, [selectedCategory]);

    const handleSave = async () => {
      if (!selectedCategory || availableParents.length === 0) return;

      // Update permissions for each parent
      for (const parent of availableParents) {
        await updateCategoryPermissions(parent.id, selectedCategory, localSettings);
      }

      setShowPermissionsModal(false);
      setSelectedCategory(null);
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Sharing Preferences</h2>
              <button
                onClick={() => {
                  setShowPermissionsModal(false);
                  setSelectedCategory(null);
                }}
                className="p-2 hover:bg-stone-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {Object.entries(sharingPermissions).map(([category, settings]) => (
              <div key={category} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-medium capitalize">
                      {category.replace(/([A-Z])/g, ' $1').trim()}
                    </h3>
                    <p className="text-sm text-stone-500">
                      Control how {category} are shared with parents
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.enabled}
                      onChange={(e) => {
                        setSelectedCategory(category);
                        setLocalSettings({ ...settings, enabled: e.target.checked });
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-stone-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                  </label>
                </div>

                {settings.enabled && settings.mode && (
                  <div className="mt-4 space-y-2">
                    <label className="text-sm font-medium">Share Mode</label>
                    <select
                      value={settings.mode}
                      onChange={(e) => {
                        setSelectedCategory(category);
                        setLocalSettings({ ...settings, mode: e.target.value });
                      }}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="all">Share All Automatically</option>
                      <option value="selective">Choose What to Share</option>
                      <option value="none">Don't Share</option>
                    </select>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="p-6 border-t">
            <button
              onClick={handleSave}
              className="w-full px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700"
            >
              Save Preferences
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-azure-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-stone-900">Parent Sharing Center</h1>
            <p className="text-stone-600 mt-1">
              Control what your parents can see and provide feedback on
            </p>
          </div>
          <div className="flex items-center gap-3">
            {unreadCount > 0 && (
              <button className="relative p-2 bg-white border rounded-lg hover:bg-stone-50">
                <Bell size={20} />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              </button>
            )}
            <button
              onClick={() => loadSharingData()}
              className="p-2 bg-white border rounded-lg hover:bg-stone-50"
              title="Refresh"
            >
              <RefreshCw size={20} />
            </button>
            <button
              onClick={() => setShowPermissionsModal(true)}
              className="px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 flex items-center gap-2"
            >
              <Settings size={18} />
              Sharing Preferences
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-4 mt-6">
          <div className="bg-white rounded-lg p-4 border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Items Shared</p>
                <p className="text-2xl font-semibold">{stats.totalShared}</p>
              </div>
              <Share2 className="text-azure-500" size={24} />
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Parents with Access</p>
                <p className="text-2xl font-semibold">{stats.parentsWithAccess}</p>
              </div>
              <Users className="text-emerald-500" size={24} />
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Comments Received</p>
                <p className="text-2xl font-semibold">{stats.totalComments}</p>
              </div>
              <MessageSquare className="text-violet-500" size={24} />
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Last Shared</p>
                <p className="text-lg font-semibold">
                  {stats.lastSharedDate
                    ? format(new Date(stats.lastSharedDate), 'MMM d')
                    : 'Never'}
                </p>
              </div>
              <Clock className="text-orange-500" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Shared Items by Category */}
      <div className="space-y-4">
        {/* Essays Section */}
        <div className="bg-white rounded-lg border">
          <button
            onClick={() => toggleCategory('essays')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-stone-50"
          >
            <div className="flex items-center gap-3">
              <FileText className="text-azure-600" size={20} />
              <h2 className="text-lg font-semibold">Essays</h2>
              <span className="px-2 py-1 bg-azure-100 text-azure-700 text-xs rounded-full">
                {getCategoryItems('essays').length}
              </span>
            </div>
            {expandedCategories.essays ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </button>

          {expandedCategories.essays && (
            <div className="px-6 pb-4 space-y-3">
              {getCategoryItems('essays').length === 0 ? (
                <p className="text-sm text-stone-500 py-4 text-center">
                  No essays shared yet. Share essays from the Essay Development Hub.
                </p>
              ) : (
                getCategoryItems('essays').map(item => (
                  <div key={item.id} className="border rounded-lg p-4 hover:shadow-sm transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-medium">{item.item_title || 'Untitled Essay'}</h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            item.version === 'final'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {item.version}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-stone-500">
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            Shared {format(new Date(item.shared_at), 'MMM d, h:mm a')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users size={14} />
                            {item.shared_with?.name || 'Parent'}
                          </span>
                          {item.viewed_at && (
                            <span className="flex items-center gap-1 text-emerald-600">
                              <Eye size={14} />
                              Viewed
                            </span>
                          )}
                        </div>
                        {item.comments && item.comments.length > 0 && (
                          <div className="mt-3 p-3 bg-azure-50 rounded-lg">
                            <p className="text-sm font-medium mb-1">Parent Feedback:</p>
                            {item.comments.map(comment => (
                              <div key={comment.id}>
                                <p className="text-sm text-stone-700">{comment.comment_text}</p>
                                <p className="text-xs text-stone-500 mt-1">
                                  — {comment.parent?.name}, {format(new Date(comment.created_at), 'MMM d')}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleRevoke('essay', item.item_id, item.shared_with_id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Revoke sharing"
                        >
                          <Lock size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Brag Sheet Section */}
        <div className="bg-white rounded-lg border">
          <button
            onClick={() => toggleCategory('bragSheet')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-stone-50"
          >
            <div className="flex items-center gap-3">
              <Award className="text-violet-600" size={20} />
              <h2 className="text-lg font-semibold">Brag Sheet</h2>
              <span className="px-2 py-1 bg-violet-100 text-violet-700 text-xs rounded-full">
                {getCategoryItems('bragSheet').length}
              </span>
            </div>
            {expandedCategories.bragSheet ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </button>

          {expandedCategories.bragSheet && (
            <div className="px-6 pb-4 space-y-3">
              {getCategoryItems('bragSheet').length === 0 ? (
                <p className="text-sm text-stone-500 py-4 text-center">
                  No brag sheet shared yet. Share your brag sheet from the Brag Sheet Framework.
                </p>
              ) : (
                getCategoryItems('bragSheet').map(item => (
                  <div key={item.id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium mb-2">{item.item_title || 'Brag Sheet'}</h3>
                        <div className="flex items-center gap-4 text-xs text-stone-500">
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            Shared {format(new Date(item.shared_at), 'MMM d, h:mm a')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users size={14} />
                            {item.shared_with?.name || 'Parent'}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRevoke('brag_sheet', item.item_id, item.shared_with_id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Revoke sharing"
                      >
                        <Lock size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Recommendations Section */}
        <div className="bg-white rounded-lg border">
          <button
            onClick={() => toggleCategory('recommendations')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-stone-50"
          >
            <div className="flex items-center gap-3">
              <MessageSquare className="text-emerald-600" size={20} />
              <h2 className="text-lg font-semibold">Recommendation Requests</h2>
              <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                {getCategoryItems('recommendations').length}
              </span>
            </div>
            {expandedCategories.recommendations ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </button>

          {expandedCategories.recommendations && (
            <div className="px-6 pb-4 space-y-3">
              {getCategoryItems('recommendations').length === 0 ? (
                <p className="text-sm text-stone-500 py-4 text-center">
                  No recommendations shared yet. Share from your Recommendations page.
                </p>
              ) : (
                getCategoryItems('recommendations').map(item => (
                  <div key={item.id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium mb-2">{item.item_title || 'Recommendation'}</h3>
                        <div className="flex items-center gap-4 text-xs text-stone-500">
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            Shared {format(new Date(item.shared_at), 'MMM d, h:mm a')}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRevoke('recommendation', item.item_id, item.shared_with_id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Revoke sharing"
                      >
                        <Lock size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Permissions Modal */}
      {showPermissionsModal && <PermissionsModal />}
    </div>
  );
};

export default StudentSharingPanel;